/**
 * PATH: packages/email-hub/src/feature/components/RecipientSuggestions.tsx
 * FILE_ID: EMAILHUB-COMPOSE-RECIPIENT-SUGGESTIONS-001
 * PURPOSE: Suggestion list for the composer recipient field.
 * WHY: Kept beside RecipientAutocomplete so that file stays under the size ceiling.
 */
import type { Suggestion } from './RecipientAutocomplete'

const SOURCE_LABEL: Record<string, string> = {
  outlook: 'Outlook',
  history: 'Recent',
  org: 'Org',
  client: 'Client',
  contact: 'Contact',
}

export function RecipientSuggestions({
  suggestions,
  highlight,
  loading,
  sourceErrors,
  onHighlight,
  onPick,
}: {
  suggestions: Suggestion[]
  highlight: number
  loading: boolean
  sourceErrors: { outlook: boolean; local: boolean }
  onHighlight: (index: number) => void
  onPick: (email: string) => void
}) {
  return (
    <ul
      role="listbox"
      className="absolute left-0 right-0 top-full mt-1 z-20 bg-popover border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto"
    >
      {suggestions.map((s, i) => {
        const active = i === highlight
        return (
          <li
            key={`${s.email}-${i}`}
            role="option"
            aria-selected={active}
            onMouseEnter={() => onHighlight(i)}
            onMouseDown={(e) => { e.preventDefault(); onPick(s.email) }}
            className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer text-sm ${
              active ? 'bg-accent' : 'hover:bg-accent/60'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-foreground truncate">
                {s.name}
                {s.client_name && s.source !== 'client' && (
                  <span className="text-muted-foreground"> - {s.client_name}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{s.email}</div>
            </div>
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-muted text-muted-foreground rounded">
              {SOURCE_LABEL[s.source] ?? s.source}
            </span>
          </li>
        )
      })}
      {loading && (
        <li className="px-3 py-1.5 text-xs text-muted-foreground" role="status">Searching…</li>
      )}
      {!loading && suggestions.length === 0 && !sourceErrors.outlook && !sourceErrors.local && (
        <li className="px-3 py-1.5 text-xs text-muted-foreground" role="status">
          No matches - press Enter to add a new address.
        </li>
      )}
      {!loading && (sourceErrors.outlook || sourceErrors.local) && (
        <li
          className="border-t border-border px-3 py-1.5 text-[11px] text-amber-500 dark:text-amber-300 bg-amber-500/5"
          role="status"
          aria-live="polite"
        >
          {sourceErrors.outlook && sourceErrors.local
            ? 'Couldn\'t load suggestions - check your connection and try again.'
            : sourceErrors.outlook
              ? 'Outlook contacts unavailable - showing local contacts only.'
              : 'Local contacts unavailable - showing Outlook only.'}
        </li>
      )}
    </ul>
  )
}
